import type { AnalysisProgress } from '@playcastdotio/Messaging';
import type { MacroVisualTestManager } from '../../services/playjector/managers/MacroVisualTestManager';

interface VisualTestResponses {
  reply(peerId: string, action: string, result: Promise<unknown>): void;
  progress(peerId: string, requestId: string, progress: AnalysisProgress): void;
}
const ACTIONS = new Set(['macroRecorderVisualTestPrepare', 'macroRecorderVisualTestStart',
  'macroRecorderVisualTestStatus', 'macroRecorderVisualTestSensitivity', 'macroRecorderVisualTestCancel']);

/** Routes the owning local editor's visual-test commands and correlated calibration progress. */
export class LocalVisualTestCommandHandler {
  public constructor(private readonly manager: MacroVisualTestManager | undefined, private readonly responses: VisualTestResponses) {}

  public dispatch(peerId: string, action: string, payload: Record<string, unknown>): boolean {
    if (!ACTIONS.has(action)) return false;
    const requestId = String(payload.requestId ?? '');
    const result = this.manager ? this.invoke(peerId, action, requestId, payload)
      : Promise.resolve({ requestId, success: false, error: 'visual_test_manager_unavailable' });
    this.responses.reply(peerId, action, result.catch(error => ({ requestId, success: false,
      error: boundedError(error) })));
    return true;
  }

  private invoke(peerId: string, action: string, requestId: string, payload: Record<string, unknown>): Promise<unknown> {
    const manager = this.manager!;
    if (action === 'macroRecorderVisualTestPrepare') {
      return manager.prepare(payload.test as never, progress => this.responses.progress(peerId, requestId, progress))
        .then(prepared => ({ requestId, success: true, ...prepared }));
    }
    if (action === 'macroRecorderVisualTestStart') {
      return manager.start(String(payload.testId ?? ''), payload.sequential === true, payload.thresholdOverrides, payload.previewSettings)
        .then(started => ({ requestId, success: true, ...started }));
    }
    if (action === 'macroRecorderVisualTestStatus') {
      return manager.status(String(payload.runId ?? '')).then(status => ({ requestId, success: true, status }));
    }
    if (action === 'macroRecorderVisualTestSensitivity') {
      return manager.setSensitivity(String(payload.runId ?? ''), payload.sensitivity, payload.rawScores)
        .then(() => ({ requestId, success: true }));
    }
    return manager.cancel(String(payload.runId ?? '')).then(success => ({ requestId, success,
      ...(success ? {} : { error: 'visual_test_cancel_unconfirmed' }) }));
  }
}

function boundedError(error: unknown): string {
  const message = error instanceof Error ? error.message : '';
  return message.length <= 128 && /^[a-z0-9_]+$/.test(message) ? message : 'visual_test_request_failed';
}
